
import vModal from "@/components/vModal.vue";
import {Component, Inject, Model, Prop, Vue, Watch} from "vue-property-decorator";
import {ExportLabelState} from "@/models/NutritionLabels/ExportLabelState";
import {mapState} from "vuex";
import {Recipe} from "@/models/Recipes/Recipe";
import {DownloadNutritionLabelContentTypes} from "@/utils/API/RequestContentTypes";
import {LabelTypeMetadata} from "@/models/NutritionLabels/LabelTypeMetadata";
import UserRepository from "@/utils/API/UserRepository";
import {SET_USER_DETAILS} from "@/store/MutationTypes";
import {CanadaFOPHighInMetadata} from "@/models/NutritionLabels/CanadaFOPHighInMetadata";
import {RecipeCompositionMeasurement} from "@/models/Recipes/RecipeCompositionMeasurement";
import {dateDisplayFormat} from "@/utils/Defaults";
import {ExportLabelView} from "@/models/Cautionary/ExportLabelView";
import {CautionaryFoodType} from "@/models/Cautionary/CautionaryFoodType";
import {CaffeinatedProductType} from "@/models/Cautionary/CaffeinatedProductType";
import {PaginatedViewManager} from "@/utils/PaginatedViewManager";
import {LabelTypes} from "@/models/NutritionLabels/LabelTypes";
import {NutritionLabelDownloadFileFormat} from "@/models/NutritionLabels/NutritionLabelDownloadFileFormat";
import vBackOfPackageConfig from "@/components/vBackOfPackageConfig.vue";
import NutritionLabelRepository from "@/utils/API/NutritionLabelRepository";
import {AxiosError} from "axios";
import RestaurantRepository from "@/utils/API/RestaurantRepository";
import { TrialRestaurantActivity } from "@/models/Restaurants/TrialRestaurantActivity";
import { UserTypes } from "@/models/User/UserTypes";
import {
  getProjectedMonthlyLabelsRemaining,
  hasLowPaidLabelCtaDismissalCookie,
  setLowPaidLabelCtaDismissalCookie,
  shouldShowLowPaidLabelCta,
} from "@/utils/Subscriptions/PaidLabelAvailability";

@Component({
  components: {
    vModal,
  },
  computed: {
    NutritionLabelDownloadFileFormat: () => NutritionLabelDownloadFileFormat,
    ...mapState({
      localizationState: (state: any) => state.LocalizationModule,
      accountStoreState: (state: any) => state.AccountModule,
    }),
  },
})
export default class vExportLabelButtons extends Vue {
  @Model() exportLabelState!: ExportLabelState;
  @Prop() packageLabelType!: LabelTypeMetadata;
  @Prop({default: 0}) roundingRuleIndex!: number;
  @Prop() previewContent: Blob;
  @Prop() isLoading!: boolean;
  @Prop() hasCautionary!: boolean;
  @Prop() isFopDisplay!: boolean;
  @Prop() activeView!: ExportLabelView;
  @Prop() recipe!: Recipe;
  @Prop() highIn!: CanadaFOPHighInMetadata;
  @Prop() canPrint!: boolean;
  @Prop() subrecipeMeasurements!: RecipeCompositionMeasurement[];
  @Prop({default: false}) shouldTrackTrialActivity!: boolean;

  // @ts-expect-error NOTE: This is in fact the valid type. TypeScript doesn't like the component type.
  @Inject("bopConfigRef") bopConfigInstance: InstanceType<vBackOfPackageConfig>;
  // @ts-expect-error NOTE: This is in fact the valid type. TypeScript doesn't like the component type.
  @Inject("fopConfigRef") fopConfigInstance: InstanceType<vFrontOfPackageConfig>;
  // @ts-expect-error NOTE: This is in fact the valid type. TypeScript doesn't like the component type.
  @Inject("csopConfigRef") csopConfigInstance: InstanceType<vCautionaryInputs>;
  // @ts-expect-error NOTE: This is in fact the valid type. TypeScript doesn't like the component type.
  @Inject("cfopConfigRef") cfopConfigInstance: InstanceType<vCautionaryFOPConfig>;

  accountStoreState: any;
  localizationState: any;
  showLearnMoreModal = false;
  showLowLabelCtaModal = false;
  hasShownLowLabelCta = false;
  lowLabelCtaRemaining = 0;
  DATE_DISP_FORMAT = dateDisplayFormat;

  disableDownload = false;
  LabelTypeMetadata = LabelTypeMetadata;

  viewManager: PaginatedViewManager = new PaginatedViewManager([
    { order: 1, key: ExportLabelView.BOP, onEntry: (fro: number) => this.bopScreenEntry(fro) },
    {
      order: 2,
      key: ExportLabelView.FOP,
      predicate: this.hasNutritionalFrontOfPack,
      onEntry: (fro, to) => this.fopScreenEntry(fro, to),
      onExit: () => this.fopScreenExit()
    },
    {
      order: 3,
      key: ExportLabelView.CautionaryBOP,
      predicate: this.hasCautionaryLabels,
      onEntry: () => this.cSopScreenEntry()
    },
    {
      order: 4,
      key: ExportLabelView.CautionaryFOP,
      predicate: this.hasCautionaryLabels,
      onEntry: () => this.cFopScreenEntry(),
      onExit: this.hideSaveAndDownloadButtons.bind(this)
    }
  ]);

  shouldShowSaveAndDownloadButtons: boolean = false;

  created() {
    console.log("Running created hooks");
    this.viewManager.init();
  }

  showSaveAndDownloadButtons() {
    this.shouldShowSaveAndDownloadButtons = true;
  }

  hideSaveAndDownloadButtons() {
    this.shouldShowSaveAndDownloadButtons = false;
  }

  async bopScreenEntry(fromPage: number) {
    if (fromPage === null) {
      if (this.hasNutritionalFrontOfPack() || this.hasCautionaryLabels()) {
        this.hideSaveAndDownloadButtons();
        return;
      }
      this.showSaveAndDownloadButtons();
    }

    await this.bopConfigInstance.fetchPreview();
  }

  async fopScreenEntry(fromPage: number, toPage: number) {
    this.fopConfigInstance.enter();
    await this.fopConfigInstance.fetchPreview();

    // Moving from BoP
    if (fromPage < toPage && !this.hasCautionaryLabels()) {
      this.showSaveAndDownloadButtons();
    }
  }

  fopScreenExit() {
    // Moving to Cautionary FoP
    this.hideSaveAndDownloadButtons();
  }

  async cSopScreenEntry() {
    await this.csopConfigInstance.fetchPreview();
  }

  async cFopScreenEntry() {
    await this.cfopConfigInstance.fetchPreview();
    this.showSaveAndDownloadButtons();
  }

  hasNutritionalFrontOfPack() {
    return (
      this.packageLabelType === LabelTypeMetadata.FrontOfPack
      || this.exportLabelState.nutritionCountry.name == "UK"
    );
  }

  hasCautionaryLabels(): boolean {
    if (this.accountStoreState.userDetails.isTrial) {
      return false;
    }

    return this.hasCautionary;
  }

  get translations() {
    return this.localizationState.translations;
  }

  get isOwner() {
    return this.accountStoreState.userDetails.userType === UserTypes.Owner;
  }

  get downloadingMultipleLabels() {
    return this.viewManager.pageCount > 1;
  }

  get remainingLabelDownloads() {
    let labelsCount = 0;
    if (this.accountStoreState.userDetails.nutritionLabelsLimit.labelsLimit != null) {
      labelsCount =
        parseInt(this.accountStoreState.userDetails.nutritionLabelsLimit.labelsLimit) -
        parseInt(this.accountStoreState.userDetails.nutritionLabelsLimit.downloadedLabels);
    }

    if (labelsCount < 0) {
      labelsCount = 0;
    }

    return labelsCount;
  }

  get projectedRemainingLabelDownloads() {
    return getProjectedMonthlyLabelsRemaining(this.remainingLabelDownloads);
  }

  get shouldShowLowLabelCta() {
    const limits = this.accountStoreState.userDetails.nutritionLabelsLimit;
    return shouldShowLowPaidLabelCta({
      monthlyLimit: limits.labelsLimit,
      monthlyLabelsRemaining: this.remainingLabelDownloads,
      paidLabelsAvailable: limits.paidLabelsAvailable,
      isSpecialAccount: this.accountStoreState.userDetails.isSpecial === true,
    });
  }

  showLowLabelCtaAfterDownload() {
    const restaurantId = this.accountStoreState.userDetails.restaurantId;
    if (
      this.hasShownLowLabelCta
      || hasLowPaidLabelCtaDismissalCookie(restaurantId)
      || !this.shouldShowLowLabelCta
    ) {
      return;
    }

    this.hasShownLowLabelCta = true;
    this.lowLabelCtaRemaining = this.projectedRemainingLabelDownloads;
    this.showLowLabelCtaModal = true;
  }

  dismissLowLabelCta() {
    setLowPaidLabelCtaDismissalCookie(this.accountStoreState.userDetails.restaurantId);
    this.showLowLabelCtaModal = false;
  }

  showOwnerOnlyAlert(action: "buy-labels" | "upgrade-package") {
    const message = action === "upgrade-package"
      ? "Only the account owner can upgrade the package."
      : "Only the account owner can purchase add-on labels.";

    this.$snotify.error(message);
  }

  handleBuyMoreLabelsClick() {
    if (!this.isOwner) {
      this.showOwnerOnlyAlert("buy-labels");
      return;
    }

    this.showLowLabelCtaModal = false;
    this.showLearnMoreModal = false;
    this.$router.push({ name: "subscription", query: { buyLabels: "1" } }).catch(() => {});
  }

  handleUpgradePackageClick() {
    if (!this.isOwner) {
      this.showOwnerOnlyAlert("upgrade-package");
      return;
    }

    this.showLowLabelCtaModal = false;
    this.showLearnMoreModal = false;
    this.$router.push({ name: "subscription" }).catch(() => {});
  }

  get hasNoLabelsRemaining() {
    const limits = this.accountStoreState.userDetails.nutritionLabelsLimit;
    return this.remainingLabelDownloads <= 0 && (limits?.paidLabelsAvailable ?? 0) <= 0;
  }

  get lowLabelModalTitle() {
    return this.hasNoLabelsRemaining
      ? "You have reached your label download limit"
      : "Your monthly labels are running low";
  }

  get lowLabelModalMessage() {
    return this.hasNoLabelsRemaining
      ? "You have used all available monthly label downloads and Label Credits."
      : "Your monthly label allowance is almost used.";
  }

  showBuyLabelsModalAfterLimitReached() {
    this.lowLabelCtaRemaining = this.projectedRemainingLabelDownloads;
    this.showLowLabelCtaModal = true;
  }

  get isCanada() {
    return this.exportLabelState.nutritionCountry.id === 0;
  }

  get isBusy() {
    return this.disableDownload || this.isLoading;
  }

  get isUSorUK() {
    return true;
    const countryId = this.exportLabelState.nutritionCountry.id;
    return countryId === 1 || countryId === 2;
  }

  shouldShowPrevButton() {
    return !this.viewManager.isFirstPage;
  }

  @Watch("viewManager.currentPageKey")
  onNutrientsList(val: ExportLabelView) {
    this.$emit('switch-view', val);
  }

  shouldShowContinueButton() {
    return !this.viewManager.isLastPage;
  }

  @Watch("hasCautionary")
  handleCautionaryValidity(newState: boolean) {
    console.log("Cautionary validity changed to " + newState);
    if (newState || this.hasNutritionalFrontOfPack()) {
      this.hideSaveAndDownloadButtons();
    }

    else {
      this.showSaveAndDownloadButtons();
    }
  }

  get isInvalidSupplementFactsData() {
    const cautionarySoPData = this.exportLabelState.labelTypes[LabelTypeMetadata.CautionarySideOfPack];

    return (
      this.isCanada
      && this.exportLabelState.nutritionLabelType == LabelTypes.SupplementLabel
      // Cautionary food type is not selected
      && (!cautionarySoPData.foodType
        // The food type is caffeinated, but the caffeinated type is not selected
        || (cautionarySoPData.foodType == CautionaryFoodType.CaffeinatedSupplementedFood && !cautionarySoPData.caffeinatedType)
        // The food type is caffeinated, the caffeinated food type is chewing gum, but the quantity has not been provided
        || (cautionarySoPData.foodType == CautionaryFoodType.CaffeinatedSupplementedFood
          && cautionarySoPData.caffeinatedType == CaffeinatedProductType.ChewingGum
          && !cautionarySoPData.quantityInPackage
        )
      )
    );
  }

  shouldDisableContinueButton() {
    return /*this.isInvalidSupplementFactsData ||*/ this.disableDownload || this.isLoading;
  }

  async markTrialFirstLabelDownloaded() {
    if (!this.shouldTrackTrialActivity || !this.$store.getters.getUserDetails.isTrial) {
      return;
    }

    try {
      await RestaurantRepository.updateCurrentTrialActivity({
        activity: TrialRestaurantActivity.DownloadedFirstLabel,
      });
      this.$emit("trial-label-downloaded");
    } catch {
      // Ignore metadata update failures so the download flow is not blocked.
    }
  }

  async downloadLabel(fileType: NutritionLabelDownloadFileFormat) {
    this.disableDownload = true;

    const renderedItems = [this.bopConfigInstance.renderModel];
    const fopRenderData = this.hasNutritionalFrontOfPack() ? this.fopConfigInstance.renderModel : null;
    const cSopRenderData = this.csopConfigInstance.renderModel;
    const cFopRenderData = this.cfopConfigInstance.renderModel;

    if (fopRenderData) {
      renderedItems.push(fopRenderData);
    }

    if (this.hasCautionaryLabels()) {
      renderedItems.push(cSopRenderData);
      renderedItems.push(cFopRenderData);
    }

    console.log(renderedItems);

    for (const item of renderedItems) {
      item.format = fileType;
    }

    const request = {
      restaurantId: this.accountStoreState.userDetails.restaurantId,
      shouldIncreaseDownloadedLabelsCount: true,
      roundingRuleIndex: this.roundingRuleIndex,
      labels: renderedItems
    };

    // get the filename
    let filename = this.recipe.name.split(" ").join("_");

    try {
      const result = await NutritionLabelRepository.downloadLabelV2(request);

      this.pushDownloadToClient(result.data, filename, result.data.type);
      this.showLowLabelCtaAfterDownload();
      await this.markTrialFirstLabelDownloaded();
    }
    catch (error) {
      const ex = error as AxiosError;
      if (ex.response?.headers["content-type"]?.includes("application/json")) {

        const text = await (ex.response.data as any).text();
        const json = JSON.parse(text);

        this.$snotify.error(json.message);
        if (this.hasNoLabelsRemaining) {
          this.showBuyLabelsModalAfterLimitReached();
        }
      }
      else {
        this.$snotify.error(ex.response?.data?.message || ex.message);
        if (this.hasNoLabelsRemaining) {
          this.showBuyLabelsModalAfterLimitReached();
        }
      }
    }
    finally {
      this.disableDownload = false;
    }
  }

  labelWidth() {
    let width: any = 0;
    let label = <HTMLImageElement>document.getElementById("labelPreview");
    width = label.naturalWidth;
    return width;
  }

  labelHeight() {
    let height: any = 0;

    let label = <HTMLImageElement>document.getElementById("labelPreview");
    height = label.naturalHeight;

    return height;
  }

  getFileContentType(chosenFileType: number): DownloadNutritionLabelContentTypes {
    if (this.packageLabelType && this.packageLabelType === LabelTypeMetadata.FrontOfPack) {
      return "application/zip";
    }

    return chosenFileType === 0 ? "application/pdf" : "image/jpeg";
  }

  getFileExtension(contentType: string) {
    if (contentType === "application/zip") return ".zip";
    if (contentType === "application/pdf") return ".pdf";
    if (contentType === "image/png") return ".png";
    if (contentType === "image/jpeg") return ".jpg";

    return "";
  }

  pushDownloadToClient(blobData: BlobPart, filename: string, fileContentType: string) {
    const blob = new Blob([blobData], {
      type: fileContentType,
    });

    const extension = this.getFileExtension(fileContentType as DownloadNutritionLabelContentTypes);
    filename += extension;

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();

    this.disableDownload = false;
    UserRepository.fetchUserInfo().then((response) => {
      this.$store.commit(SET_USER_DETAILS, {
        userDetails: response.data.result,
        logged: true,
      });
    });
  }
}
